import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { cn } from '@shared/lib/cn';
import { platform } from '@shared/lib/platform';

/**
 * Restrained button system. Variants follow the design spec:
 *   - primary  : filled brand-primary, ink-on-primary text — top-of-page CTA
 *   - secondary: hairline-bordered card surface — secondary action
 *   - ghost    : transparent with hover wash — tertiary action / nav rows
 *   - success  : filled success — the affirming end of a two-way choice
 *   - danger   : filled danger — irreversible / destructive
 *   - link     : text-only with underline on hover — inline copy actions
 *
 * Press feedback: scale 0.98 + 4% darken, 120ms (per motion tokens).
 * Native haptic feedback fires on `light` impact unless suppressed.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-sm font-sans text-label font-bold transition-colors transition-transform duration-fast ease-smooth select-none active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 disabled:grayscale-[0.2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-700 text-[color:#ffffff] hover:bg-primary-800 disabled:bg-sunken disabled:text-ink-muted',
        gold:
          'bg-brass text-[color:var(--accent-brass-ink)] font-semibold hover:opacity-90 disabled:bg-sunken disabled:text-ink-muted',
        secondary:
          'bg-card hairline text-ink hover:bg-sunken disabled:bg-sunken disabled:text-ink-muted',
        ghost:
          'bg-transparent text-ink hover:bg-sunken disabled:text-ink-muted',
        success:
          'bg-success text-[color:#ffffff] hover:opacity-90 disabled:bg-sunken disabled:text-ink-muted',
        danger:
          'bg-danger text-[color:#ffffff] hover:opacity-90 disabled:bg-sunken disabled:text-ink-muted',
        link:
          'bg-transparent text-primary-600 underline-offset-4 hover:underline px-0 disabled:text-ink-muted',
      },
      size: {
        sm: 'h-9 px-3 text-caption',
        md: 'h-11 px-5',
        lg: 'h-14 px-6 text-body',
        icon: 'h-11 w-11 rounded-pill',
      },
      width: {
        auto: '',
        full: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      width: 'auto',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Suppress haptic feedback on press (e.g. on rapidly-repeated presses). */
  haptic?: false;
  /** Render a spinner instead of children when loading. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, width, asChild, haptic, loading, onClick, children, disabled, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (haptic !== false && platform.isNative()) {
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => { /* noop */ });
    }
    onClick?.(event);
  };

  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, width }), className)}
      onClick={handleClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Spinner /> : children}
    </Comp>
  );
});

function Spinner(): JSX.Element {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
    </svg>
  );
}
